import React from 'react';
import { Link } from 'react-router';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';

// Helpers
import { confirm, getURLSegment, toggleDrawer } from '../../helpers/helpers.js'

// Translations
import translate from '../Translate.js';

// Dependencies
import moment from 'moment';

// Components
import * as icons from '../Icons/Icons';
import Drawer from '../Drawer/Drawer';
import ExamStatus from './ExamStatus';
import StudentStatus from '../Modules/StudentStatus';
import ExamDetails from '../Modules/ExamDetails';
import ExamDetailedInfo from '../Modules/ExamDetailedInfo';
import ExamFlags from './ExamFlags';
import ExamAttachmentsList from '../Modules/ExamAttachmentsList';
import StudentOverview from '../Modules/StudentOverview';

const ExamDashboard = observer(class ExamDashboard extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.getExam = this.getExam.bind(this);
    this.syncExam = this.syncExam.bind(this);
    this.getEnrollments = this.getEnrollments.bind(this);
    this.getExamFlags = this.getExamFlags.bind(this);
  }

  componentWillMount() {
    const { role } = this.props.store.SessionStore.userData;
    const { SessionStore } = this.props.store;

    if(role.toLowerCase() === 'learner') {
      this.props.routing.push({
        pathname: 'unauthorized'
      });
    }

    this.state = {
      drawerOpen: false,
      drawerContent: null,
      fundingRuleSid: null,
      enrollmentName: null,
      location: null,
      sessionSid: null,
      userSid: null,
      changeRequest: false,
      readonly: false,
      scheduled: false,
      crossListed: false,
      parentSchool: null
    }

    // Get exam sid from URL
    const examSid = getURLSegment(this, 2);

    // Get data with the exam sid
    this.getExam(examSid);
    this.getEnrollments(examSid);
    this.getExamFlags(examSid);

    // Make sure we have a list of proctor types
    SessionStore.getProctorTypes();
  }

  componentDidUpdate() {
    const { history } = this.props.routing;
    const { pathname, query } = this.props.routing.location;
    const { SessionStore } = this.props.store;
    const { translation } = this.props;

    // Check if we should display a success message
    if(query.toaster === 'examSaved') {
      history.push({
        pathname: pathname,
        search: null
      });
      SessionStore.showToaster(translation.exam_saved, 'success');
    }
  }

  getExam(sid) {
    this.props.store.ExamStore.getExam(sid);
  }

  getEnrollments(sid) {
    this.props.store.ExamStore.getEnrollments(sid);
  }

  getExamFlags(sid) {
    this.props.store.ExamStore.getExamFlags(sid);
  }

  syncExam() {
    const _this = this;
    const { translation } = this.props;
    const { ExamStore, SessionStore, SyncStore } = this.props.store;
    const { sid } = ExamStore.exam;

    const data = {
      type: 'exams',
      sid: sid
    }

    const callback = () => {
      _this.getExam(sid);
      SessionStore.showToaster(translation.sync_scheduled, 'success');
    }

    SyncStore.scheduleSync(data, callback);
  }

  deleteExam(exam) {
    const { translation } = this.props;
    const { ExamStore, SessionStore } = this.props.store;

    const msg = {
      heading: translation.delete_confirm.heading,
      body: translation.delete_confirm.body,
      button: translation.delete_confirm.button
    }

    const callback = () => {
      SessionStore.showToaster(translation.exam_deleted, 'success');
      this.props.routing.push('/');
    }

    confirm(
      msg,
      () => ExamStore.deleteExam(exam.sid, exam.courseSid, callback)
    );
  }

  unlockExam(examSid, configurationSid) {
    const data = {
      approved: true,
      locked: false
    }

    let callback = () => this.getExam(examSid);

    this.props.store.ExamStore.updateConfigurationStatus(examSid, configurationSid, data, callback);
  }

  lockExam(examSid, configurationSid) {
    const data = {
      approved: true,
      locked: true
    }

    let callback = () => this.getExam(examSid);

    this.props.store.ExamStore.updateConfigurationStatus(examSid, configurationSid, data, callback);
  }

  initChangeRequest(configurationSid) {
    const drawerContent = 'ExamException';

    this.setState({
      drawerContent,
      defaultConfigSid: configurationSid,
      changeRequest: true
    });

    toggleDrawer(null, this, drawerContent);
  }

  editChangeRequest(changeRequestSid) {
    let { pathname } = this.props.routing.location;
    const drawerContent = 'EditExamException';

    // Edit an accommodation
    pathname += `?changeRequest=${changeRequestSid}`;
    this.props.routing.push(pathname);

    this.setState({
      drawerContent,
      configurationSid: changeRequestSid,
      changeRequest: true
    });

    toggleDrawer(null, this, drawerContent);
  }

  render() {
    const { SessionStore, SettingsStore } = this.props.store;
    const { translation } = this.props;
    const { ExamStore } = this.props.store;
    const { exam, examEnrollments, examFlags } = ExamStore;
    const { apiExamId, synchronization } = exam.configuration;
    const { userTimezone } = this.props.store.SessionStore;

    let scheduleCloseDate = exam.configuration.scheduleCloseDate;

    const flags = toJS(examFlags);

    if(moment(scheduleCloseDate).local().format('M-D-YYYY') === '1-1-2070') {
      // If there’s no scheduling window set, use the exam close date
      scheduleCloseDate = exam.configuration.examCloseDate;
    }

    if(typeof exam !== 'undefined' && typeof exam.sid !== 'undefined') {
      return (
        <main tabIndex="0" id="main-content" className="page">
          <header className="page__header">
            <div className="wrap">
              <h1 className="page__title">{exam.title}</h1>

              <SessionStore.validater>
                <div className="page__actions actions">
                  {/* Funding rules button */}
                  <Link
                    iamAction="sp:DescribeFundingRules"
                    iamResource={`ssrn:ss:sp:us-east:${SessionStore.userData.installSid}:funding`}
                    className="btn--outline"
                    to={`${getURLSegment(this, 1)}/${getURLSegment(this, 2)}?fundingRuleScope=examSid&scopeSid=${exam.sid}`}
                    onClick={() => {toggleDrawer(null, this, 'FundingRules')}}
                  >
                    {translation.funding_rules}
                  </Link>

                  {/* Edit exam button (if the exam isn’t locked) */}
                  {!exam.configuration.locked &&
                    <Link
                      iamAction="sp:ModifyExam"
                      iamResource={[
                        `ssrn:ss:sp:us-east:${SessionStore.userData.installSid}:course/${SessionStore.userData.courseSid}`,
                        `ssrn:ss:sp:us-east:${SessionStore.userData.installSid}:exam/${exam.sid}`
                      ]}
                      to={`exams/edit/${exam.sid}`}
                      className="actions__btn actions__btn--secondary"
                    >
                      <icons.iconEdit />
                      {translation.edit_exam_button}
                    </Link>
                  }

                  {/* Change request button (if the exam is locked) */}
                  {
                    exam.configuration.locked &&
                    SessionStore.userData.role.toLowerCase() !== 'administrator'  &&
                    !exam.configuration.referenceExamConfigurationSid &&
                    <button
                      className="actions__btn actions__btn--secondary"
                      onClick={() => {this.initChangeRequest(exam.configuration.sid)}}
                    >
                      <icons.iconEdit />
                      {translation.change_request_button}
                    </button>
                  }

                  {/* Delete exam button */}
                  {
                    !exam.configuration.locked &&
                    <button
                      className="btn--outline btn--negative"
                      onClick={() => this.deleteExam(exam)}
                    >
                      <icons.iconTrash />
                      {translation.delete_exam_button}
                    </button>
                  }

                  {exam.configuration.locked && SessionStore.userData.role.toLowerCase() === 'administrator' &&
                    <button
                      className="actions__btn actions__btn--secondary"
                      onClick={() => {this.unlockExam(exam.sid, exam.configuration.sid)}}
                    >
                      <icons.iconUnlock />
                      {translation.unlock}
                    </button>
                  }

                  {!exam.configuration.locked &&
                    SessionStore.userData.role.toLowerCase() === 'administrator' &&
                    SettingsStore.settings.requireManagerExamApproval &&
                    <button
                      className="actions__btn actions__btn--secondary"
                      onClick={() => {this.lockExam(exam.sid, exam.configuration.sid)}}
                    >
                      <icons.iconLock />
                      {translation.lock}
                    </button>
                  }
                </div>
              </SessionStore.validater>
            </div>
          </header>

          <div className="wrap">
            <div className="page__data">
              {/* Show sync stats if they exist */}
              {
                apiExamId &&
                synchronization &&
                synchronization.exam &&

                <div className="box box--right">
                  <p>
                    <span className="title">
                      {translation.last_sync}:&nbsp;

                      {!synchronization.exam.lastRun &&
                        translation.never
                      }

                      {synchronization.exam.lastRun &&
                        moment.utc(synchronization.exam.lastRun)
                              .local()
                              .tz(userTimezone)
                              .format('M/D/YYYY [at] h:mma z')}
                    </span>

                    {synchronization.exam.nextRun &&
                      <span className="title">
                        {translation.next_sync}:&nbsp;

                        {moment.utc(synchronization.exam.nextRun)
                               .local()
                               .tz(userTimezone)
                               .format('M/D/YYYY [at] h:mma z')}
                      </span>
                    }

                    <button
                      className="btn--link"
                      onClick={this.syncExam}
                    >
                      {translation.sync_now}
                    </button>
                  </p>
                </div>
              }
            </div>


            {/* Note that exam is awaiting approval */}
            {!exam.configuration.approved &&
              <div className="cta cta--negative">
                <strong className="cta__copy">{translation.pending_cta.heading}</strong>
                <span className="cta__subcopy">{translation.pending_cta.body}</span>
              </div>
            }

            {/* Note that change request is awaiting approval */}
            {exam.configuration.referenceExamConfigurationSid &&
              <div className="cta cta--negative">
                <strong className="cta__copy">{translation.request_pending_cta.heading}</strong>
                <span className="cta__subcopy">{translation.request_pending_cta.body}</span>

                <button
                  className="cta__btn btn--outline btn--negative"
                  onClick={() => {this.editChangeRequest(exam.configuration.referenceExamConfigurationSid)}}
                >
                  {translation.request_pending_cta.button}
                </button>
              </div>
            }

            <ExamStatus
              parent={this}
              locked={exam.configuration.locked}
              incidents={exam.openSessionEvents}
              pending={exam.pendingApprovals}
              flags={flags.timesOccurred}
              {...this.props}
            />

            <article className="panel">
              <div className="panel__body">
                <StudentStatus summary={exam.summary} i={0} limitedScope={exam.isLimitedScope} {...this.props} />

                <ExamDetails
                  parent={this}
                  examDashboard={true}
                  exam={exam}
                  scheduleWindow={true}
                  scheduleCloseDate={scheduleCloseDate}
                  {...this.props}
                />

                <ExamAttachmentsList
                  parent={this}
                  examDashboard={true}
                  {...this.props}
                  exam={exam}
                />

                <ExamDetailedInfo
                  {...this.props}
                  exam={exam}
                />

                {flags && flags.timesOccurred > 0 &&
                  <ExamFlags
                    {...this.props}
                    parent={this}
                    flags={flags}
                  />
                }

                {SessionStore.loadingCalls.indexOf('ExamEnrollments') < 0 &&
                  <StudentOverview
                    parent={this}
                    {...this.props}
                    exam={exam}
                    examEnrollments={examEnrollments}
                    configurationSid={exam.configuration.sid}
                    getExam={this.getExam}
                  />
                }
              </div>
            </article>
          </div>

          <Drawer
            content={this.state.drawerContent}
            isOpen={this.state.drawerOpen}
            parent={this}
            getExam={this.getExam}
            getEnrollments={this.getEnrollments}
            exam={exam}
            fundingRuleSid={this.state.fundingRuleSid}
            preserveDrawer={true}
            enrollmentName={this.state.enrollmentName}
            searchForStudent={true}
            location={this.state.location}
            sessionSid={this.state.sessionSid}
            closePath={`${getURLSegment(this, 1)}/${getURLSegment(this, 2)}`}
            toUserSid={this.state.userSid}
            examSid={exam.sid}
            examConfigurationSid={this.state.configurationSid}
            defaultConfigSid={this.state.defaultConfigSid}
            changeRequest={this.state.changeRequest}
            contextType="examSid"
            contextId={exam.sid}
            preventReload={true}
            readonly={this.state.readonly}
            scheduled={this.state.scheduled}
            crossListed={this.state.crossListed}
            parentSchool={this.state.parentSchool}
            workflowModule={this.state.workflowModule}
            {...this.props}
          />
        </main>
      )
    }

    return null;
  }
})

export default translate('ExamDashboard')(ExamDashboard);



// WEBPACK FOOTER //
// ./src/app/components/ExamDashboard/ExamDashboard.js